/**
 * Regras puras de "o que pede atenção" na lista de atendimento (sem React, testáveis).
 *
 * Uma conversa pede atenção quando o CLIENTE tem a última palavra e quem deveria responder é a
 * equipe (humano), não a IA. Quanto mais tempo parada, mais alto o placar. O placar é uma
 * heurística determinística e explicável (cada ponto vem de um motivo escrito em `reason`): a
 * IA entra como camada opcional por cima (ver `AtendimentoLive`), nunca como único critério.
 */
import type { PlatformConversation } from "../../api/types.js";

export type SortMode = "ia" | "atencao" | "recentes" | "antigas";

/** Nota da triagem por IA de uma conversa (0–100) e o motivo em uma frase. */
export interface TriageNote { urgency: number; reason: string }

export const SORT_LABELS: Record<SortMode, string> = {
  ia: "Prioridade da IA",
  atencao: "Precisam de atenção",
  recentes: "Mais recentes",
  antigas: "Mais antigas"
};

export type AttentionLevel = "critical" | "high" | "waiting" | "none";

export interface Attention {
  level: AttentionLevel;
  /** Placar de ordenação (0 = nada pendente). */
  score: number;
  /** Minutos desde a última mensagem do cliente; `null` = ninguém esperando. */
  waitingMinutes: number | null;
  /** Motivo legível, mostrado ao operador (ex.: "aguardando há 3 h · sem responsável"). */
  reason: string;
}

/** Limites do SLA visual (minutos). Acima de `HIGH` = alta; acima de `CRITICAL` = crítica. */
export const ATTENTION_HIGH_MINUTES = 30;
export const ATTENTION_CRITICAL_MINUTES = 120;

/**
 * Quem escreveu por último. A lista do WhatsApp não carrega mensagens, então o servidor manda
 * `lastSender`; sem ele, olha a última mensagem carregada; sem nenhuma, `null` (desconhecido).
 */
export function lastSenderOf(conversation: Pick<PlatformConversation, "messages"> & { lastSender?: string | null }): string | null {
  return conversation.lastSender ?? conversation.messages.at(-1)?.sender ?? null;
}

/**
 * A equipe deve uma resposta? Cliente falou por último E a IA não está respondendo (modo humano,
 * fila de humano ou IA fora do piloto). `aiAnswers` = a IA responderia sozinha esta conversa.
 * Sem `lastSender` conhecido, não-lidas contam como "cliente falou por último".
 */
export function awaitingTeam(conversation: PlatformConversation, aiAnswers: boolean): boolean {
  if (aiAnswers) return false;
  const sender = lastSenderOf(conversation);
  if (sender) return sender === "customer";
  return (conversation.unreadCount ?? 0) > 0;
}

/** Minutos entre `since` e `now` (nunca negativo); `null` para data inválida. */
export function minutesSince(since: string | null | undefined, now: Date): number | null {
  if (!since) return null;
  const time = Date.parse(since);
  if (Number.isNaN(time)) return null;
  return Math.max(0, Math.floor((now.getTime() - time) / 60_000));
}

/** 45 s → "45 s"; 5 min; 1 h 05; 2 d 3 h. Usado nas estatísticas e nos selos de espera. */
export function formatWait(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ${String(minutes % 60).padStart(2, "0")}`;
  return `${Math.floor(hours / 24)} d ${hours % 24} h`;
}

export function attentionOf(conversation: PlatformConversation, now: Date, aiAnswers = false): Attention {
  if (!awaitingTeam(conversation, aiAnswers)) return { level: "none", score: 0, waitingMinutes: null, reason: "" };
  const lastMessageAt = conversation.messages.at(-1)?.createdAt ?? conversation.lastMessageAt ?? conversation.updatedAt;
  const minutes = minutesSince(lastMessageAt, now) ?? 0;
  const unassigned = !conversation.assigneeId;
  const unread = conversation.unreadCount ?? 0;

  // Placar: espera pesa mais (até 60 pts em 12 h), depois "sem dono" e mensagens acumuladas.
  let score = Math.min(minutes, 720) / 720 * 60;
  if (minutes >= ATTENTION_HIGH_MINUTES) score += 10;
  if (minutes >= ATTENTION_CRITICAL_MINUTES) score += 10;
  if (unassigned) score += 15;
  score += Math.min(unread, 5) * 1;
  score = Math.round(score * 10) / 10;

  const level: AttentionLevel = minutes >= ATTENTION_CRITICAL_MINUTES ? "critical" : minutes >= ATTENTION_HIGH_MINUTES ? "high" : "waiting";
  const parts = [`aguardando há ${formatWait(minutes * 60)}`];
  if (unassigned) parts.push("sem responsável");
  if (unread > 1) parts.push(`${unread} mensagens novas`);
  return { level, score, waitingMinutes: minutes, reason: parts.join(" · ") };
}

/** Ordena sem mexer na lista original. `atencao` põe quem espera mais no topo; o resto vai por recência. */
export function sortConversations(conversations: PlatformConversation[], mode: SortMode, now: Date, aiAnswers: (conversation: PlatformConversation) => boolean = () => false, triage?: ReadonlyMap<string, TriageNote>): PlatformConversation[] {
  const time = (conversation: PlatformConversation) => conversation.messages.at(-1)?.createdAt ?? conversation.lastMessageAt ?? conversation.updatedAt;
  const byRecent = (a: PlatformConversation, b: PlatformConversation) => time(b).localeCompare(time(a));
  const list = [...conversations];
  if (mode === "recentes") return list.sort(byRecent);
  if (mode === "antigas") return list.sort((a, b) => -byRecent(a, b));
  // "ia": a nota da IA (70%) manda, a regra fixa (30%) desempata e cobre conversa ainda não triada.
  const scored = new Map(list.map((conversation) => {
    const base = attentionOf(conversation, now, aiAnswers(conversation)).score;
    const note = mode === "ia" ? triage?.get(conversation.id) : undefined;
    return [conversation.id, note ? note.urgency * 0.7 + base * 0.3 : mode === "ia" ? base * 0.3 : base];
  }));
  return list.sort((a, b) => (scored.get(b.id)! - scored.get(a.id)!) || byRecent(a, b));
}

/** Primeiro nome para o selo ("Maria Silva" → "Maria"). */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}
